import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PoweredBy } from "@/components/PoweredBy";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, ImagePlus, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BE = process.env.REACT_APP_BACKEND_URL;
const err = (e) => e?.response?.data?.detail || "Something went wrong.";
const TS = { New: "bg-blue-100 text-blue-700", Assigned: "bg-indigo-100 text-indigo-700", "In Progress": "bg-amber-100 text-amber-700", "On Hold": "bg-slate-200 text-slate-600", Resolved: "bg-emerald-100 text-emerald-700", Closed: "bg-slate-200 text-slate-500", Reopened: "bg-rose-100 text-rose-700" };
export const Pill = ({ s }) => <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TS[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;

export default function CabinTickets() {
  const navigate = useNavigate();
  const token = localStorage.getItem("sf_cabin_token");
  const cabin = (() => { try { return JSON.parse(localStorage.getItem("sf_cabin") || "null"); } catch { return null; } })();
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "", issue: "", description: "", priority: "Medium", photos: [] });
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reopen, setReopen] = useState("");

  const load = () => axios.get(`${API}/public/cabin/my/tickets`, H).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    axios.get(`${API}/public/cabin/my/ticket-config`, H).then((r) => setCfg(r.data)).catch(() => toast.error("Session expired")); 
    load();
  }, []);

  const issues = cfg?.categories?.find((c) => c.name === f.category)?.issues || [];
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await axios.post(`${API}/public/cabin/my/uploads`, fd, { headers: { ...H.headers, "Content-Type": "multipart/form-data" } }); setF((p) => ({ ...p, photos: [...p.photos, data.url] })); toast.success("Photo added"); }
    catch (e2) { toast.error(err(e2)); }
  };
  const submit = async () => {
    if (!f.category || !f.issue) return toast.error("Select category and issue");
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/cabin/my/tickets`, f, H); toast.success(`Raised ${data.ticket_number}`); setOpen(false); setF({ category: "", issue: "", description: "", priority: "Medium", photos: [] }); load(); }
    catch (e2) { toast.error(err(e2)); } finally { setBusy(false); }
  };
  const confirmClose = async () => { try { await axios.post(`${API}/public/cabin/my/tickets/${detail.id}/confirm`, {}, H); toast.success("Ticket closed"); setDetail(null); load(); } catch (e2) { toast.error(err(e2)); } };
  const doReopen = async () => { if (!reopen.trim()) return toast.error("Please add a reason"); try { await axios.post(`${API}/public/cabin/my/tickets/${detail.id}/reopen`, { reason: reopen }, H); toast.success("Ticket reopened"); setReopen(""); setDetail(null); load(); } catch (e2) { toast.error(err(e2)); } };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-navy-900 px-5 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/cabin-portal")} className="text-blue-100/80 hover:text-white flex items-center gap-1.5 text-sm" data-testid="cabin-back-btn"><ArrowLeft size={16} /> Portal</button>
        {cabin?.logo_url ? <CompanyLogo logoUrl={cabin.logo_url} className="h-7" light /> : <span className="text-white font-semibold text-sm">{cabin?.company}</span>}
      </header>
      <main className="max-w-lg mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-display text-xl font-bold text-navy-900">Service Requests</h1>
          <Button onClick={() => setOpen(true)} data-testid="raise-ticket-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1" /> Raise</Button>
        </div>
        {!list ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-navy-900" /></div> : list.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500 text-sm">No service requests yet. Tap <b>Raise</b> to create one.</div>
        ) : (
          <div className="space-y-3">
            {list.map((t) => (
              <button key={t.id} onClick={() => setDetail(t)} data-testid={`ticket-card-${t.ticket_number}`} className="w-full text-left bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition-all">
                <div className="flex items-center justify-between"><span className="font-mono text-xs text-slate-400">{t.ticket_number}</span><Pill s={t.status} /></div>
                <p className="font-semibold text-navy-900 mt-1">{t.issue}</p>
                <p className="text-xs text-slate-500">{t.category} · {t.priority} priority</p>
              </button>
            ))}
          </div>
        )}
        <div className="mt-8 flex justify-center"><PoweredBy /></div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Raise Service Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Category *</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v, issue: "" })}>
                <SelectTrigger className="mt-1" data-testid="ticket-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{cfg?.categories?.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Issue *</Label>
              <Select value={f.issue} onValueChange={(v) => setF({ ...f, issue: v })} disabled={!f.category}>
                <SelectTrigger className="mt-1" data-testid="ticket-issue"><SelectValue placeholder={f.category ? "Select issue" : "Select category first"} /></SelectTrigger>
                <SelectContent>{issues.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {cfg?.allow_priority_choice && (
              <div><Label>Priority</Label>
                <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                  <SelectTrigger className="mt-1" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{cfg?.priorities?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Description</Label><Textarea data-testid="ticket-desc" className="mt-1" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Describe the issue…" /></div>
            <div>
              <Label>Photo</Label>
              <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2 cursor-pointer text-sm text-slate-500 hover:bg-slate-50" data-testid="ticket-photo-label">
                <ImagePlus size={16} /> Add Photo
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} />
              </label>
              <div className="flex gap-2 mt-2 flex-wrap">{f.photos.map((p, i) => <img key={i} src={`${BE}${p}`} alt="" className="h-14 w-14 object-cover rounded-lg border" />)}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="submit-ticket-btn" className="bg-navy-900">{busy ? <Loader2 className="animate-spin" size={16} /> : "Raise Service Request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><span className="font-mono text-sm text-slate-400">{detail.ticket_number}</span><Pill s={detail.status} /></DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div><p className="font-semibold text-navy-900">{detail.issue}</p><p className="text-slate-500">{detail.category} · {detail.priority} priority · {detail.department}</p></div>
                {detail.description && <p className="text-slate-600 bg-slate-50 rounded-lg p-3">{detail.description}</p>}
                {detail.photos?.length > 0 && <div className="flex gap-2 flex-wrap">{detail.photos.map((p, i) => <img key={i} src={`${BE}${p}`} alt="" className="h-16 w-16 object-cover rounded-lg border" />)}</div>}
                {detail.status === "Resolved" && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <p className="text-xs font-semibold text-emerald-700 uppercase">Resolution</p>
                    <p className="text-navy-900 mt-1">{detail.resolution_notes}</p>
                    {detail.resolution_photos?.map((p, i) => <img key={i} src={`${BE}${p}`} alt="" className="h-16 w-16 object-cover rounded-lg border mt-2" />)}
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={confirmClose} data-testid="confirm-close-btn" className="bg-emerald-600 hover:bg-emerald-700">Confirm &amp; Close</Button>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1 mb-2"><Clock size={12} /> Timeline</p>
                  <div className="space-y-2 border-l-2 border-slate-100 pl-3">
                    {detail.timeline?.map((e, i) => (
                      <div key={i} className="text-xs"><span className="text-slate-400">{new Date(e.ts).toLocaleString()}</span><p className="text-navy-900 font-medium">{e.action}</p>{e.detail && <p className="text-slate-500">{e.detail}</p>}</div>
                    ))}
                  </div>
                </div>
                {["Resolved", "Closed"].includes(detail.status) && (
                  <div className="pt-2 border-t border-slate-100">
                    <Label className="text-xs">Not resolved? Reopen with a reason</Label>
                    <Textarea className="mt-1" rows={2} value={reopen} onChange={(e) => setReopen(e.target.value)} data-testid="reopen-reason" placeholder="Issue still occurring…" />
                    <Button size="sm" variant="outline" onClick={doReopen} data-testid="reopen-btn" className="mt-2 border-rose-200 text-rose-600 hover:bg-rose-50">Reopen Ticket</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
